// محرّك حاسبة صغير للمبالغ — كسور وعمليات، بأولوية صحيحة.
//
// لماذا لا `eval`؟ لأنه ينفّذ أي شيء يصل إليه. المدخل هنا من لوحة أزرار
// محدودة، لكن الاعتماد على ذلك حجّة هشّة: يكفي خطأ واحد لاحقاً يمرّر نصاً
// من مكان آخر. المحلّل الصريح أقصر من تبرير eval وأأمن منه.
//
// الحالة: { tokens: [عدد, مُعامل, عدد, ...], current: 'نص الرقم قيد الكتابة' }
// فصل `current` عن `tokens` يجعل الفاصلة العشرية والحذف يعملان على الرقم
// الجاري وحده بلا إعادة تحليل السلسلة كلها في كل ضغطة.

export const OPS = ['+', '−', '×', '÷'];

const MAX_DIGITS = 9;
const MAX_VALUE = 9_999_999_999;

export function create(){
  return { tokens: [], current: '' };
}

/** حالة مهيّأة برقم — لتعديل حركة قائمة. */
export function fromNumber(n){
  if (n == null || n === '') return create();
  const num = Number(n);
  if (!Number.isFinite(num)) return create();
  // نحذف الأصفار الزائدة: 55.00 تُعرض 55 لا "55.00"
  return { tokens: [], current: String(Math.round(num * 100) / 100) };
}

export function isEmpty(s){
  return s.tokens.length === 0 && s.current === '';
}

/** هل ينتظر مُعامل رقماً بعده؟ (تعبير ناقص) */
export function isPending(s){
  return s.current === '' && s.tokens.length > 0
      && typeof s.tokens[s.tokens.length - 1] === 'string';
}

export function press(s, key){
  const t = s.tokens.slice();
  let cur = s.current;

  if (key === '⌫'){
    if (cur !== ''){
      cur = cur.slice(0, -1);
    } else if (t.length){
      const last = t.pop();
      // حذف رقم يعيده قابلاً للتعديل بدل محوه كاملاً
      if (typeof last === 'number') cur = String(last);
    }
    return { tokens: t, current: cur };
  }

  if (key === 'C') return create();

  if (key === '.'){
    if (cur === '') return { tokens: t, current: '0.' };
    if (cur.includes('.')) return s;              // فاصلة واحدة لكل رقم
    return { tokens: t, current: cur + '.' };
  }

  if (OPS.includes(key)){
    if (cur !== ''){
      const n = Number(cur);
      if (!Number.isFinite(n)) return s;
      t.push(round2(n), key);
      return { tokens: t, current: '' };
    }
    if (t.length === 0) return s;                 // لا مُعامل في البداية
    const last = t[t.length - 1];
    if (typeof last === 'string'){
      t[t.length - 1] = key;                      // استبدال مُعامل بمُعامل
      return { tokens: t, current: '' };
    }
    t.push(key);
    return { tokens: t, current: '' };
  }

  // رقم
  if (/^\d$/.test(key)){
    const digits = cur.replace(/[.]/g, '').length;
    if (digits >= MAX_DIGITS) return s;
    // منزلتان عشريتان كحدّ أقصى — المبالغ لا تحتاج أكثر
    const dot = cur.indexOf('.');
    if (dot >= 0 && cur.length - dot > 2) return s;
    if (cur === '0') return { tokens: t, current: key };   // 0 ثم 5 = 5 لا 05
    return { tokens: t, current: cur + key };
  }

  if (key === '00'){
    if (cur === '' || cur === '0') return s;
    return press(press(s, '0'), '0');
  }

  return s;
}

/**
 * القيمة المحسوبة، أو null إن كان التعبير غير صالح (قسمة على صفر مثلاً).
 * المُعامل المعلّق في النهاية يُتجاهل، فـ «60 +» تساوي 60.
 */
export function value(s){
  const seq = s.tokens.slice();
  if (s.current !== '' && s.current !== '.' && s.current !== '0.'){
    const n = Number(s.current);
    if (Number.isFinite(n)) seq.push(n);
  }
  while (seq.length && typeof seq[seq.length - 1] === 'string') seq.pop();
  if (!seq.length) return null;

  // المرور الأول: الضرب والقسمة (أولوية أعلى)
  const flat = [seq[0]];
  for (let i = 1; i < seq.length; i += 2){
    const op = seq[i];
    const n = seq[i + 1];
    if (n === undefined) break;
    if (op === '×'){
      flat[flat.length - 1] = flat[flat.length - 1] * n;
    } else if (op === '÷'){
      if (n === 0) return null;                   // قسمة على صفر
      flat[flat.length - 1] = flat[flat.length - 1] / n;
    } else {
      flat.push(op, n);
    }
  }

  // المرور الثاني: الجمع والطرح
  let acc = flat[0];
  for (let i = 1; i < flat.length; i += 2){
    const op = flat[i];
    const n = flat[i + 1];
    if (n === undefined) break;
    if (op === '+') acc += n;
    else if (op === '−') acc -= n;
  }

  if (!Number.isFinite(acc)) return null;
  if (Math.abs(acc) > MAX_VALUE) return null;
  return round2(acc);
}

/** التعبير كما كُتب — يُعرض فوق النتيجة ليرى المستخدم ما يحسبه. */
export function expression(s){
  const parts = s.tokens.map(t => (typeof t === 'number' ? fmtNum(t) : t));
  if (s.current !== '') parts.push(s.current);
  return parts.join(' ');
}

/** هل يحتوي التعبير على عملية حسابية فعلية؟ (لعرض سطر التعبير عند الحاجة فقط) */
export function hasOperation(s){
  return s.tokens.some(t => typeof t === 'string');
}

function round2(n){
  // نمرّ بالتقريب لا بالقصّ: 0.1 + 0.2 يجب أن تعطي 0.3 لا 0.30000000000000004
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function fmtNum(n){
  return String(round2(n));
}

/** نص العرض للنتيجة — بفواصل آلاف ومنزلتين عند الحاجة فقط. */
export function formatValue(n){
  if (n == null) return '—';
  const rounded = round2(n);
  const hasFraction = Math.abs(rounded % 1) > 0;
  return rounded.toLocaleString('en-US', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2
  });
}
